import Accordion from '@service-victoria/component-library/components/Accordion';
import ChevronDown from '@service-victoria/component-library/components/Icons/ChevronDown';
import React, { useCallback, useEffect, useRef, useState, useLayoutEffect } from 'react';

import AccordionTitle from 'Components/AccordionTitle';
import useFeatureToggles from 'hooks/useFeatureToggles';
import useModal from 'hooks/useModal';
import { Guest, PrimaryContact, TrackCheckInEvent } from 'types/checkin';

import AccordionCheckinForm, {
  AccordionCheckinFormHandle,
  PersonFormValue
} from './AccordionCheckinForm';
import { Separator, FocusedOverlay } from './styled';

export interface SelectedAccordionItem {
  index: number;
  type: 'guest' | 'primary';
}

interface AccordionCheckinProps {
  primaryContact: PrimaryContact;
  guests: Guest[];
  setGuests: React.Dispatch<React.SetStateAction<Guest[]>>;
  syncSingleCheckinView: (contact: PrimaryContact) => void;
  onSelectedAccordionChange?: (selected: SelectedAccordionItem | undefined) => void;
  disabledOnAccordionItemClick?: boolean;
  trackCheckinEvent: TrackCheckInEvent;
}

const AccordionCheckin: React.FC<AccordionCheckinProps> = ({
  primaryContact,
  guests,
  setGuests,
  syncSingleCheckinView,
  onSelectedAccordionChange,
  disabledOnAccordionItemClick,
  trackCheckinEvent
}) => {
  const numberOfGuests = guests.filter(guest => !guest.isNew).length;
  const hasNewGuest = guests.some(guest => guest.isNew);
  const modal = useModal();
  const focusActiveAccordion = useFeatureToggles('focusActiveAccordion');

  const activeDirtyForm = useRef<{ formRef: AccordionCheckinFormHandle } | null>(null);
  const [selectedAccordionItem, setSelectedAccordionItem] = useState<SelectedAccordionItem>();

  const onUpdateFormRef = useCallback((formRef: AccordionCheckinFormHandle | null) => {
    if (formRef) {
      activeDirtyForm.current = { formRef };
    } else {
      activeDirtyForm.current = null;
    }
  }, []);

  useEffect(() => {
    if (hasNewGuest) {
      const index = guests.length - 1;
      setSelectedAccordionItem({
        index,
        type: 'guest'
      });
    }
  }, [guests.length, hasNewGuest]);

  useEffect(() => {
    onSelectedAccordionChange?.(selectedAccordionItem);
  }, [onSelectedAccordionChange, selectedAccordionItem]);

  useEffect(() => {
    return () => {
      onSelectedAccordionChange?.(undefined);
    };
  }, [onSelectedAccordionChange]);

  useLayoutEffect(() => {
    if (hasNewGuest) {
      // Retrieving the accordion title html component
      // and scroll to it everytime there is a new guest
      const newGuestAccordion = document.getElementById(
        `acc-guest-${guests[guests.length - 1].id}`
      );

      if (newGuestAccordion) {
        window.scrollTo({
          top: window.scrollY + newGuestAccordion.getBoundingClientRect().top - 100
        });
      }
    }
  }, [guests, hasNewGuest]);

  const updateGuest = useCallback(
    (formValue: PersonFormValue, guest: Guest) => {
      setGuests(currentGuests => {
        return currentGuests.map(({ isNew, ...currentGuest }) => {
          if (currentGuest.id === guest.id) {
            return {
              ...currentGuest,
              ...formValue
            };
          }

          return currentGuest;
        });
      });

      setSelectedAccordionItem(undefined);
    },
    [setGuests]
  );

  const removeGuest = useCallback(
    (guest: Guest) => {
      setGuests(currentGuests =>
        currentGuests.filter(currentGuest => currentGuest.id !== guest.id)
      );
      setSelectedAccordionItem(undefined);
    },
    [setGuests]
  );

  const updatePrimaryContact = useCallback(
    (formValue: PersonFormValue) => {
      syncSingleCheckinView(formValue);
      setGuests(guests =>
        guests.map(guest => {
          return {
            ...guest,
            lastName: guest.isLastNameSetToDefaultValue ? formValue.lastName : guest.lastName,
            contactNumber: guest.isContactNumberSetToDefaultValue
              ? formValue.contactNumber
              : guest.contactNumber
          };
        })
      );

      setSelectedAccordionItem(undefined);
    },
    [setGuests, syncSingleCheckinView]
  );

  const removeGuestModal = (guest: Guest) => {
    modal.open(
      {
        children: 'Are you sure you want to remove this person from the check-in?',
        title: 'Remove'
      },
      {
        type: 'CONFIRMATION',
        confirmationOptions: {
          actions: [
            {
              onClick: () => {
                removeGuest(guest);
                modal.close();
                trackCheckinEvent('checkInGuestRemoved');
              },
              label: 'Remove'
            }
          ]
        },
        cancel: {
          text: 'Cancel',
          onClick: () => {
            modal.close();
          }
        }
      }
    );
  };

  const openConfirmationModalForNewGuest = ({
    accordionToBeSelected,
    guest
  }: {
    accordionToBeSelected?: SelectedAccordionItem;
    guest: Guest;
  }) => {
    modal.open(
      {
        children: 'Do you want to discard your changes?',
        title: 'Discard changes?'
      },
      {
        type: 'CONFIRMATION',
        confirmationOptions: {
          actions: [
            {
              onClick: () => {
                removeGuest(guest);
                modal.close();

                setSelectedAccordionItem(accordionToBeSelected);
              },
              label: 'Yes'
            }
          ]
        },
        cancel: {
          text: 'No',
          onClick: () => {
            modal.close();
          }
        }
      }
    );
  };

  const openConfirmationModalForUpdatedDetails = ({
    accordionToBeSelected,
    guest
  }: {
    accordionToBeSelected?: SelectedAccordionItem;
    guest?: Guest;
  }) => {
    modal.open(
      {
        children: 'You have unsaved changes, do you want to save?',
        title: 'Save Changes?'
      },
      {
        type: 'CONFIRMATION',
        confirmationOptions: {
          actions: [
            {
              onClick: async () => {
                const isFormValid = await activeDirtyForm.current?.formRef.isFormValid();

                if (activeDirtyForm.current && isFormValid) {
                  if (guest) {
                    updateGuest(activeDirtyForm.current.formRef.getFormValues(), guest);
                  } else {
                    updatePrimaryContact(activeDirtyForm.current.formRef.getFormValues());
                  }

                  setSelectedAccordionItem(accordionToBeSelected);
                }

                modal.close();
              },
              label: 'Yes'
            }
          ]
        },
        cancel: {
          text: 'No',
          onClick: () => {
            activeDirtyForm.current?.formRef.resetToDefaultValue();
            modal.close();
            setSelectedAccordionItem(accordionToBeSelected);
          }
        }
      }
    );
  };

  const openConfirmationModal = ({
    accordionToBeSelected,
    guest
  }: {
    accordionToBeSelected?: SelectedAccordionItem;
    guest?: Guest;
  } = {}) => {
    if (guest?.isNew) {
      openConfirmationModalForNewGuest({
        guest,
        accordionToBeSelected
      });
    } else {
      openConfirmationModalForUpdatedDetails({ guest, accordionToBeSelected });
    }
  };

  const onExpand = (type: SelectedAccordionItem['type']) => {
    if (disabledOnAccordionItemClick) {
      return () => undefined;
    }

    return (index: number) => {
      if (!activeDirtyForm.current) {
        setSelectedAccordionItem({
          index,
          type
        });
      } else {
        openConfirmationModal({
          accordionToBeSelected: {
            type,
            index
          },
          guest:
            selectedAccordionItem?.type === 'guest'
              ? guests[selectedAccordionItem.index]
              : undefined
        });
      }
    };
  };

  const onCollapse = (index: number) => {
    if (disabledOnAccordionItemClick) {
      return;
    }

    if (!activeDirtyForm.current) {
      setSelectedAccordionItem(undefined);
    } else {
      openConfirmationModal({
        guest: selectedAccordionItem?.type === 'guest' ? guests[index] : undefined
      });
    }
  };

  return (
    <>
      {focusActiveAccordion && selectedAccordionItem && !modal.isOpen && (
        <FocusedOverlay data-testid='FocussedAccordionOverlay' />
      )}
      <Accordion
        activeIndices={
          selectedAccordionItem?.type === 'primary' ? [selectedAccordionItem.index] : []
        }
        defaultActiveIndex={[]}
        disableUpdateStateOnClick={true}
        expandIcon={ChevronDown}
        contractIcon={ChevronDown}
        onExpand={onExpand('primary')}
        onCollapse={onCollapse}
        accordionItemRaisedOnActive={!!focusActiveAccordion}
        items={[
          {
            id: 'primary',
            title: function PrimaryTitle({ isActive }) {
              return (
                <AccordionTitle
                  person={primaryContact}
                  isActive={isActive}
                  isPrimary={true}
                  data-testid={`PrimaryAccordionTitle`}
                />
              );
            },
            component: function MyComponent(handler) {
              if (handler.isActive) {
                return (
                  <AccordionCheckinForm
                    onUpdateFormRef={onUpdateFormRef}
                    firstName={{
                      value: primaryContact.firstName
                    }}
                    lastName={{
                      value: primaryContact.lastName
                    }}
                    contactNumber={{
                      value: primaryContact.contactNumber
                    }}
                    primaryAction={{
                      text: 'Update details',
                      onClick: updatePrimaryContact
                    }}
                  />
                );
              }

              return null;
            }
          }
        ]}
      />
      {numberOfGuests > 0 && (
        <Separator data-testid='NumberOfGuests'>
          + {numberOfGuests} other{numberOfGuests > 1 ? 's' : ''}
        </Separator>
      )}
      <Accordion
        activeIndices={selectedAccordionItem?.type === 'guest' ? [selectedAccordionItem.index] : []}
        disableUpdateStateOnClick={true}
        expandIcon={ChevronDown}
        contractIcon={ChevronDown}
        onExpand={onExpand('guest')}
        onCollapse={onCollapse}
        accordionItemRaisedOnActive={!!focusActiveAccordion}
        items={guests.map((guest, index) => ({
          id: `guest-${guest.id}`,
          title: function GuestTitle({ isActive }) {
            return (
              <AccordionTitle
                person={guest}
                isActive={isActive}
                data-testid={`GuestAccordionTitle${index}`}
              />
            );
          },
          component: function MyComponent(handler) {
            if (handler.isActive) {
              return (
                <AccordionCheckinForm
                  forNewGuest={guest.isNew}
                  onUpdateFormRef={onUpdateFormRef}
                  firstName={{
                    value: guest.firstName
                  }}
                  lastName={{
                    checkbox: {
                      label: 'Family name is',
                      defaultChecked: guest.isLastNameSetToDefaultValue ?? true
                    },
                    value: guest.lastName,
                    defaultValue: primaryContact.lastName
                  }}
                  contactNumber={{
                    checkbox: {
                      label: 'Phone number is',
                      defaultChecked: guest.isContactNumberSetToDefaultValue ?? true
                    },
                    value: guest.contactNumber,
                    defaultValue: primaryContact.contactNumber
                  }}
                  primaryAction={{
                    text: guest.isNew ? 'Add to check-in' : 'Update details',
                    onClick: personFormValue => {
                      updateGuest(personFormValue, guest);
                      if (guest.isNew) {
                        trackCheckinEvent('checkInGuestAdded');
                      }
                    }
                  }}
                  secondaryAction={{
                    text: guest.isNew ? 'Cancel' : 'Remove from check-in',
                    onClick: () => {
                      if (guest.isNew) {
                        removeGuest(guest);
                      } else {
                        removeGuestModal(guest);
                      }
                    }
                  }}
                />
              );
            }

            return null;
          }
        }))}
      />
    </>
  );
};

export default AccordionCheckin;
